function AVLNode(tree, value) {
    this._tree = tree;
    this._height = 1;

    this._parent = null;
    this._left = null;
    this._right = null;

    const mapped = tree.map === null ? value : tree.map(value);
    this._value = value;
    this._mapped = mapped;
    this._combined = tree.combine === null ? null : mapped;
}

AVLNode.prototype = {
    left: function() {
        return this._left;
    },

    right: function() {
        return this._right;
    },

    next: function() {
        let node = this;

        if (node._right !== null) {
            node = node._right;
            while (node._left !== null) {
                node = node._left;
            }
            return node;
        }

        while (node._parent !== null && node !== node._parent._left) {
            node = node._parent;
        }
        return node._parent;
    },

    previous: function() {
        let node = this;

        if (node._left !== null) {
            node = node._left;
            while (node._right) {
                node = node._right;
            }
            return node;
        }

        while (node._parent !== null && node !== node._parent._right) {
            node = node._parent;
        }
        return node._parent;
    },

    update: function(value) {
        const tree = this._tree;
        if (tree !== null) {
            update(tree, this, value);
        } else {
            this._value = value;
            this._mapped = value;
            this._combined = null;
        }
    },

    remove: function() {
        const tree = this._tree;
        if (tree !== null) {
            remove(tree, this);
        }
    },

    value: function() {
        return this._value;
    },

    mapped: function() {
        return this._mapped;
    },

    combined: function() {
        return this._combined;
    }
};

function factor(node) {
    const left = node._left;
    const right = node._right;
    return (left === null ? 0 : left._height) - (right === null ? 0 : right._height);
}

function height(node) {
    const left = node._left;
    const right = node._right;
    const leftHeight = left === null ? 0 : left._height;
    const rightHeight = right === null ? 0 : right._height;
    return (leftHeight < rightHeight ? rightHeight : leftHeight) + 1;
}

function combined(tree, node) {
    let combined = node._mapped;

    const left = node._left;
    if (left !== null) {
        combined = tree.combine(left._combined, combined);
    }

    const right = node._right;
    if (right !== null) {
        combined = tree.combine(combined, right._combined);
    }

    return combined;
}

function recombine(tree, node) {
    if (tree.combine === null) {
        return;
    }

    while (node !== null) {
        node._combined = combined(tree, node);
        node = node._parent;
    }
}

function replace(tree, node, withNode) {
    const parent = node._parent;
    if (withNode !== null) {
        withNode._parent = parent;
    }

    if (parent !== null) {
        if (node === parent._left) {
            parent._left = withNode;
        } else {
            parent._right = withNode;
        }
    } else {
        tree._root = withNode;
    }
}

function rotate(tree, node, leftRotation) {
    const pivot = leftRotation ? node._right : node._left;
    replace(tree, node, pivot);

    node._parent = pivot;
    if (leftRotation) {
        node._right = pivot._left;
        if (node._right !== null) {
            node._right._parent = node;
        }
        pivot._left = node;
    } else {
        node._left = pivot._right;
        if (node._left !== null) {
            node._left._parent = node;
        }
        pivot._right = node;
    }

    if (tree.combine !== null) {
        pivot._combined = node._combined;
        node._combined = combined(tree, node);
    }

    node._height = height(node);
    pivot._height = height(pivot);
    return pivot;
}

function rebalance(tree, node) {
    for (;;) {
        const oldHeight = node._height;
        node._height = height(node);

        const balance = factor(node);
        if (balance < -1) {
            const right = node._right;
            if (factor(right) > 0) {
                rotate(tree, right, false);
            }
            node = rotate(tree, node, true);
        } else if (balance > 1) {
            const left = node._left;
            if (factor(left) < 0) {
                rotate(tree, left, true);
            }
            node = rotate(tree, node, false);
        }

        if (oldHeight === node._height) {
            break;
        }

        const parent = node._parent;
        if (parent === null) {
            break;
        }
        node = parent;
    }

    if (node._parent === null) {
        tree._root = node;
    }
}

function insert(tree, node) {
    const value = node._value;
    let parent = tree._root;

    while (parent !== null) {
        if (tree.cmp(value, parent._value) < 0) {
            const left = parent._left;
            if (left === null) {
                parent._left = node;
                break;
            }
            parent = left;
        } else {
            const right = parent._right;
            if (right === null) {
                parent._right = node;
                break;
            }
            parent = right;
        }
    }

    if (parent !== null) {
        node._parent = parent;
        recombine(tree, parent);
        rebalance(tree, parent);
    } else {
        node._parent = null;
        tree._root = node;
    }
}

function remove(tree, node) {
    const left = node._left;
    const right = node._right;
    const parent = node._parent;

    if (!right || !left) {
        replace(tree, node, right || left);

        if (parent) {
            recombine(tree, parent);
            rebalance(tree, parent);
        }
    } else if (!right._left) {
        replace(tree, node, right);

        left._parent = right;
        right._left = left;
        right._height = node._height;

        recombine(tree, right);
        rebalance(tree, right);
    } else {
        let leftmost = right._left;
        while (leftmost._left) {
            leftmost = leftmost._left;
        }

        const above = leftmost._parent;
        above._left = leftmost._right;
        if (leftmost._right) {
            leftmost._right._parent = above;
        }

        replace(tree, node, leftmost);

        right._parent = leftmost;
        left._parent = leftmost;
        leftmost._right = right;
        leftmost._left = left;
        leftmost._height = node._height;

        recombine(tree, above);
        rebalance(tree, above);
    }

    node._tree = null;
    node._height = 1;

    node._parent = null;
    node._left = null;
    node._right = null;

    node._mapped = node._value;
    node._combined = null;
}

function update(tree, node, value) {
    const mapped = tree.map === null ? value : tree.map(value);

    const next = node.next();
    const prev = node.previous();
    if ((next !== null && tree.cmp(next._value, value) < 0) || (prev !== null && tree.cmp(value, prev._value) < 0)) {
        remove(tree, node);

        node._value = value;
        node._mapped = mapped;
        node._combined = tree.combine === null ? null : mapped;
        node._tree = tree;

        insert(tree, node);
    } else {
        node._value = value;
        node._mapped = mapped;
        recombine(tree, node);
    }
}

function AVLTree() {
}

AVLTree.prototype = {
    _root: null,

    cmp: function(left, right) {
        if (left < right) {
            return -1;
        } else if (right < left) {
            return 1;
        }
        return 0;
    },

    map: null,

    combine: null,

    root: function() {
        return this._root;
    },

    combined: function(_default) {
        const root = this._root;
        if (root === null) {
            return _default === void 0 ? null : _default;
        }
        return root.combined();
    },

    insert: function(value) {
        const node = new AVLNode(this, value);
        insert(this, node);
        return node;
    },

    min: function() {
        let node = this._root;
        if (node !== null) {
            while (node._left !== null) {
                node = node._left;
            }
        }
        return node;
    },

    max: function() {
        let node = this._root;
        if (node !== null) {
            while (node._right !== null) {
                node = node._right;
            }
        }
        return node;
    },

    find: function(compared) {
        let node = this._root;
        let result = 0;

        while (node !== null && (result = this.cmp(compared, node._value)) !== 0) {
            node = result < 0 ? node._left : node._right;
        }

        return node;
    }
};

export default AVLTree;



// WEBPACK FOOTER //
// ./src/abusesa-js/AVLTree.js